import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api-client';
import { useSignup } from '@/features/auth/hooks';
import { passwordIsStrong } from '@/features/auth/password';
import { PasswordRulesList } from '@/features/auth/PasswordHints';
import {
  AuthAlert,
  AuthButton,
  AuthField,
  AuthInput,
  AuthShell,
  FieldError,
  PasswordWrap,
  type AuthAlertState,
} from './login';

/**
 * Signup route — design ported 1:1 from `frontend/pages/signup.html`. Reuses
 * the auth-shell + primitives from `./login` so both pages stay visually
 * identical, then layers the password-policy checklist + "passwords match"
 * hint on top.
 */

const signupSchema = z
  .object({
    email: z.string().min(1, 'Email is required').email('Enter a valid email.'),
    access_code: z.string().min(1, 'Access code is required'),
    password: z
      .string()
      .refine(passwordIsStrong, {
        message:
          'Password must meet every rule below (12+ chars, upper, lower, number, special).',
      }),
    confirm_password: z.string().min(1, 'Confirm your password'),
  })
  .refine((d) => d.password === d.confirm_password, {
    message: 'Passwords do not match.',
    path: ['confirm_password'],
  });
type SignupForm = z.infer<typeof signupSchema>;

const searchSchema = z.object({
  email: z.string().optional().catch(undefined),
  code: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/signup')({
  validateSearch: searchSchema,
  component: SignupPage,
});

function SignupPage() {
  const { email: emailFromInvite, code: codeFromInvite } = Route.useSearch();
  const signup = useSignup();
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [alert, setAlert] = useState<AuthAlertState>(null);

  const form = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: emailFromInvite ?? '',
      access_code: codeFromInvite ?? '',
      password: '',
      confirm_password: '',
    },
    mode: 'onBlur',
  });

  // Re-hydrate defaults if invite query params arrive after first render.
  useEffect(() => {
    if (emailFromInvite || codeFromInvite) {
      form.reset({
        email: emailFromInvite ?? '',
        access_code: codeFromInvite ?? '',
        password: form.getValues('password'),
        confirm_password: form.getValues('confirm_password'),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailFromInvite, codeFromInvite]);

  const pw = form.watch('password');
  const confirm = form.watch('confirm_password');

  const onSubmit = async (values: SignupForm) => {
    setAlert(null);
    try {
      await signup.mutateAsync(values);
      window.location.assign('/login?signedup=1');
    } catch (err) {
      setAlert({
        kind: 'error',
        text:
          err instanceof ApiError && err.status === 400
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Could not create the account. Please try again.',
      });
    }
  };

  return (
    <AuthShell>
      <h1
        className="text-navy text-[34px] leading-tight m-0 mb-1.5"
        style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
      >
        Create your account
      </h1>
      <p className="text-muted text-[15px] m-0 mb-[26px]">
        Confirm your invite details and choose a password
      </p>

      <AuthAlert alert={alert} />

      <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
        <AuthField label="Email Address" htmlFor="email" required>
          <AuthInput
            id="email"
            type="email"
            autoComplete="username"
            placeholder="name@yourcompany.com"
            {...form.register('email')}
          />
          <FieldError message={form.formState.errors.email?.message} />
        </AuthField>

        <AuthField label="Access Code" htmlFor="access_code" required>
          <AuthInput
            id="access_code"
            type="text"
            autoComplete="off"
            placeholder="Paste the code from your invite email"
            {...form.register('access_code')}
          />
          <p className="mt-1.5 text-[13px] text-muted m-0">
            This was included in your invite link and expires once you sign up.
          </p>
          <FieldError message={form.formState.errors.access_code?.message} />
        </AuthField>

        <AuthField label="Password" htmlFor="password" required>
          <PasswordWrap show={showPw} onToggle={() => setShowPw((s) => !s)}>
            <AuthInput
              id="password"
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Create a strong password"
              {...form.register('password')}
            />
          </PasswordWrap>
          <PasswordRulesList password={pw} />
          <FieldError message={form.formState.errors.password?.message} />
        </AuthField>

        <AuthField label="Confirm Password" htmlFor="confirm_password" required>
          <PasswordWrap show={showConfirm} onToggle={() => setShowConfirm((s) => !s)}>
            <AuthInput
              id="confirm_password"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Re-enter your password"
              {...form.register('confirm_password')}
            />
          </PasswordWrap>
          <MatchHint password={pw} confirm={confirm} />
          <FieldError message={form.formState.errors.confirm_password?.message} />
        </AuthField>

        <AuthButton type="submit" disabled={signup.isPending}>
          {signup.isPending ? 'Creating account…' : 'Create Account'}
        </AuthButton>
      </form>

      <p className="mt-[22px] text-sm text-muted text-center m-0">
        Already set up?{' '}
        <Link
          to="/login"
          className="text-brand-blue font-semibold no-underline hover:underline"
        >
          Log in
        </Link>
      </p>
    </AuthShell>
  );
}

// ── Live hints ──────────────────────────────────────────────────────────

function MatchHint({ password, confirm }: { password: string; confirm: string }) {
  if (!confirm) return null;
  const ok = password === confirm;
  return (
    <p
      className={cn(
        'm-0 mt-2 text-[12px]',
        ok ? 'text-state-success-text' : 'text-state-error-text',
      )}
    >
      {ok ? 'Passwords match.' : 'Passwords do not match.'}
    </p>
  );
}
